/**
 * Backend 403 response structure when feature is not available
 */
export interface FeatureNotAvailableResponse {
	error: 'feature_not_available';
	feature: string;
	currentPlan: string;
	requiredPlan: string;
	message: string;
}

/**
 * Backend 429 response structure when feature limit is reached
 */
export interface FeatureLimitReachedResponse {
	error: 'feature_limit_reached';
	feature: string;
	limit: number;
	used: number;
	resetsAt: string;
	message: string;
}

/**
 * Error thrown when a feature is not available on the user's plan
 */
export class FeatureNotAvailableError extends Error {
	public readonly data: FeatureNotAvailableResponse;

	constructor(data: FeatureNotAvailableResponse) {
		super(`Feature ${data.feature} not available on ${data.currentPlan} plan`);
		this.name = 'FeatureNotAvailableError';
		this.data = data;
	}
}

/**
 * Error thrown when a feature's usage limit has been reached
 */
export class FeatureLimitReachedError extends Error {
	public readonly data: FeatureLimitReachedResponse;

	constructor(data: FeatureLimitReachedResponse) {
		super(`Feature ${data.feature} limit reached (${data.used}/${data.limit})`);
		this.name = 'FeatureLimitReachedError';
		this.data = data;
	}
}

/**
 * Type guard to check if error response is a feature not available error
 */
export const isFeatureNotAvailableResponse = (data: any): data is FeatureNotAvailableResponse => {
	return data?.error === 'feature_not_available';
};

/**
 * Type guard to check if error response is a feature limit reached error
 */
export const isFeatureLimitReachedResponse = (data: any): data is FeatureLimitReachedResponse => {
	return data?.error === 'feature_limit_reached';
};
